import React, { useState, useEffect, useRef } from "react";
import { Search, Clock, User, Package, CheckCircle, XCircle, Navigation, Trash2, Edit2, Calendar, Repeat, Loader2, Plus, AlertCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { getPickups, createAdminPickup, getAllUsers, getWasteItems, getTimeSlots, updatePickupStatus, deletePickup, getWorkers, addRecurringCollection, getPickupHistory, deleteCollectionRecord, updateCollectionStatus, getDisposalSites } from "@/services/api";

export const Pickups = () => {
  const [pickups, setPickups] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [headerSourceFilter, setHeaderSourceFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [mainTab, setMainTab] = useState("one-time");
  const [selectedPickup, setSelectedPickup] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedWorkerId, setSelectedWorkerId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [editableAddress, setEditableAddress] = useState("");
  const [editableLocationInput, setEditableLocationInput] = useState("");
  const [initialLocationInput, setInitialLocationInput] = useState("");
  const isLocationManuallyEditedRef = useRef(false);
  const [editableDate, setEditableDate] = useState("");
  const [remark, setRemark] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [pickupHistory, setPickupHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [newRecurringPickups, setNewRecurringPickups] = useState([]);
  const [modifiedSavedCollections, setModifiedSavedCollections] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Scheduling state
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    userIds: [],
    pickup_type: "normal",
    waste_type: "",
    scheduled_date: "",
    scheduled_time_slot: "",
    notes: "",
    amount: ""
  });
  const [usersList, setUsersList] = useState([]);
  const [wasteTypes, setWasteTypes] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  
  const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);
  const [scheduleResultData, setScheduleResultData] = useState(null);
  
  const listRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const fetchInitialData = async () => {
      try {
        const workersData = await getWorkers();
        if (isMounted) {
          if (workersData?.success) setWorkers(workersData.workers || []);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    fetchInitialData();

    return () => { isMounted = false; };
  }, []);

  const fetchPickups = async () => {
    try {
      setLoading(true);
      // Pass the selectedDate to getPickups
      const data = await getPickups(500, null, selectedDate);
      if (data && data.success) {
        setPickups(data.pickups || []);
      }
    } catch (error) {
      console.error("Error fetching pickups:", error);
      toast.error("Failed to load pickups.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPickups();
  }, [selectedDate]);

  const getFilteredPickups = (type) => {
    return pickups.filter(p => {
      const isRecurring = p.pickup_type === 'recurring';
      if (type === 'normal' && isRecurring) return false;
      if (type === 'recurring' && !isRecurring) return false;

      const user = p.user_name || p.user || "";
      const address = p.address || (p.location && p.location.address) || "";
      const worker = p.worker_name || p.worker || "";
      
      const matchesSearch = user.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           address.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           worker.toLowerCase().includes(searchQuery.toLowerCase());
      
      let normalizedStatus = p.status;
      if (normalizedStatus === "requested") normalizedStatus = "pending";
      else if (normalizedStatus === "assigned" || normalizedStatus === "accepted") normalizedStatus = "in-progress";
      else if (normalizedStatus === "collected" || normalizedStatus === "completed" || normalizedStatus === "disposed") normalizedStatus = "completed";
      else if (normalizedStatus === "cancelled") normalizedStatus = "cancelled";
      
      const matchesTab = activeTab === "all" || normalizedStatus === activeTab;
      
      const isSourceMatch = headerSourceFilter === "all" 
        || (headerSourceFilter === "admin" && p.createdByAdmin)
        || (headerSourceFilter === "app" && !p.createdByAdmin);

      return matchesSearch && matchesTab && isSourceMatch;
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "collected":
      case "completed":
      case "disposed": return "bg-green-100 text-green-800 border-green-200";
      case "in-progress":
      case "assigned":
      case "accepted": return "bg-blue-100 text-blue-800 border-blue-200";
      case "requested":
      case "pending": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "cancelled": return "bg-red-100 text-red-800 border-red-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handleApplyChanges = async () => {
    if (!selectedPickup) return;
    
    // Validation
    if (selectedStatus === 'cancelled' && !remark.trim()) {
      return toast.error("Remark is required when cancelling a pickup.");
    }
    
    const isWorkerAssigned = selectedWorkerId && selectedWorkerId !== "unassigned";
    const hadWorkerAssigned = selectedPickup.worker_id || selectedPickup.worker;
    
    if (!['requested', 'cancelled'].includes(selectedStatus)) {
      if (!isWorkerAssigned && !hadWorkerAssigned) {
        return toast.error("A worker (captain) must be assigned for this status.");
      }
    }

    // Date Validation
    const initialDate = selectedPickup.scheduled_date || "";
    if (editableDate !== initialDate && editableDate) {
      const today = new Date().toISOString().split('T')[0];
      if (editableDate < today) {
        return toast.error("Scheduled date must be a future date (or today).");
      }
    }

    setIsSaving(true);
    const id = selectedPickup._id || selectedPickup.id;
    try {
      // 1. Save general changes (only sending changed fields)
      const payload = {};
      
      if (selectedStatus !== selectedPickup.status) {
        payload.status = selectedStatus;
      }
      
      const trimmedLocationInput = (editableLocationInput || "").trim();
      const trimmedInitialLocationInput = (initialLocationInput || "").trim();
      const hasValidCoords =
        selectedPickup.location &&
        Number(selectedPickup.location.lat) !== 0 &&
        Number(selectedPickup.location.lng) !== 0 &&
        Boolean(selectedPickup.location.lat) &&
        Boolean(selectedPickup.location.lng);
      // Send Exact Location when admin actually changed the autofilled value,
      // or when the pickup still has no valid coordinates (so the autofilled
      // Address / Step-2 location string gets geocoded on Apply). Skipping an
      // unchanged value when coords already exist avoids needlessly triggering
      // strict geocode verification (which rejects APPROXIMATE addresses) on
      // unrelated edits like captain assignment.
      if (
        trimmedLocationInput !== "" &&
        (trimmedLocationInput !== trimmedInitialLocationInput || !hasValidCoords)
      ) {
        payload.location = trimmedLocationInput;
      }
      
      // amount is not tracked by separate state, so if selectedPickup.amount changed from original, handle it
      const currentAmountStr = (selectedPickup.amount ?? "").toString();
      const originalAmountStr = (selectedPickup.original_amount ?? "").toString();
      if (currentAmountStr !== originalAmountStr) {
        payload.amount = selectedPickup.amount === "" ? null : Number(selectedPickup.amount);
      }

      if (editableDate !== (selectedPickup.scheduled_date || "")) {
        payload.scheduled_date = editableDate;
      }

      if (selectedWorkerId !== (selectedPickup.worker_id || selectedPickup.worker || "unassigned")) {
        if (selectedWorkerId !== "unassigned") {
          payload.agentId = selectedWorkerId;
        } else {
          payload.agentId = null; // Or handle unassignment if supported
        }
      }

      if (selectedStatus === 'cancelled' && remark !== selectedPickup.remark) {
        payload.remark = remark;
      }
      
      const initialAddress = selectedPickup.address || selectedPickup.location?.address || "";
      if (editableAddress !== initialAddress) {
        payload.address = editableAddress;
      }
      
      // If no valid changes to standard fields, we still might need to update recurring collections below
      // but let's check if payload has keys before sending the main update
      let updatedPickupData = null;
      if (Object.keys(payload).length > 0) {
        const updateRes = await updatePickupStatus(id, payload);
        if (!updateRes.success) {
           throw new Error(updateRes.detail || updateRes.message || "Failed to update pickup");
        }
        updatedPickupData = updateRes.data;
      }

      // 2. Save new recurring collections if any
      if (selectedPickup.pickup_type === 'recurring' && newRecurringPickups.length > 0) {
        for (const np of newRecurringPickups) {
          const colPayload = {
            worker_id: np.worker_id === "unassigned" ? null : np.worker_id,
            status: np.status,
            collected_at: new Date(np.date).toISOString(),
            weight_kg: 0,
            coins_earned: 0
          };
          await addRecurringCollection(id, colPayload);
        }
      }

      // 3. Save modified status for existing collection records
      const modifiedIds = Object.keys(modifiedSavedCollections);
      if (modifiedIds.length > 0) {
        for (const colId of modifiedIds) {
          await updateCollectionStatus(colId, modifiedSavedCollections[colId]);
        }
      }

      if (updatedPickupData) {
        // If the date was changed and no longer matches the currently viewed date, remove it from the list
        if (updatedPickupData.scheduled_date && updatedPickupData.scheduled_date !== selectedDate && selectedPickup.pickup_type !== 'recurring') {
          setPickups(prev => prev.filter(p => p._id !== id && p.id !== id));
        } else {
          setPickups(prev => prev.map(p => (p._id === id || p.id === id) ? updatedPickupData : p));
        }
      } else {
        // Fallback: If only recurring collections were updated, just refresh the list or manually update
        const updatedPickups = await getPickups(500, null, selectedDate);
        if (updatedPickups && updatedPickups.success) {
          setPickups(updatedPickups.pickups || []);
        }
      }

      toast.success("Pickup updated successfully");
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error applying changes:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to apply changes";
      toast.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePickup = async (id) => {
    if (!window.confirm("Delete this pickup?")) return;
    try {
      await deletePickup(id);
      setPickups(prev => prev.filter(p => (p._id !== id && p.id !== id)));
      toast.success("Pickup deleted");
    } catch (error) {
      toast.error("Failed to delete pickup");
    }
  };

  const fetchPickupHistory = async (pickupId) => {
    setLoadingHistory(true);
    try {
      const data = await getPickupHistory(pickupId);
      if (data && data.success) {
        setPickupHistory(data.history || []);
      }
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const openEditDialog = (pickup) => {
    setSelectedPickup(pickup);
    setSelectedWorkerId(pickup.worker_id || pickup.worker || "unassigned");
    setSelectedStatus(pickup.status);
    const addressVal = pickup.address || pickup.location?.address || "";
    // Admin-scheduled flow: autofill Exact Location with Address.
    // App-scheduled flow: fill Exact Location with the location string
    // collected in App schedule-pickup Step 2 (pickup.location.address).
    let exactLocationVal = "";
    if (pickup.createdByAdmin) {
      exactLocationVal = addressVal;
    } else {
      exactLocationVal = pickup.location?.address || pickup.address || "";
    }
    setEditableAddress(addressVal);
    setEditableLocationInput(exactLocationVal);
    setInitialLocationInput(exactLocationVal);
    isLocationManuallyEditedRef.current = false;
    setEditableDate(pickup.scheduled_date || "");
    setRemark(pickup.remark || "");
    setPickupHistory([]);
    setNewRecurringPickups([]);
    setModifiedSavedCollections({});
    if (pickup.pickup_type === 'recurring') {
      fetchPickupHistory(pickup._id || pickup.id);
    }
    setIsDialogOpen(true);
  };

  const handleAddRecurringPickupUI = () => {
    // Validation: cannot add if there's any entry in Current Pickup section
    const hasActiveStaged = newRecurringPickups.some(p => !['completed', 'disposed'].includes(p.status));
    const hasActiveSaved = pickupHistory.some(p => !['completed', 'disposed'].includes(p.status));
    
    if (hasActiveStaged || hasActiveSaved) {
      toast.error("Complete or Dispose the current pickup before adding a new entry.");
      return;
    }

    const newEntry = {
      id: `temp-${Date.now()}`,
      worker_id: "unassigned",
      status: "requested",
      date: new Date().toISOString().split('T')[0],
      isNew: true
    };
    setNewRecurringPickups([...newRecurringPickups, newEntry]);
  };

  const updateNewRecurringPickup = (tempId, field, value) => {
    setNewRecurringPickups(prev => prev.map(p => p.id === tempId ? { ...p, [field]: value } : p));
  };

  const removeNewRecurringPickup = (tempId) => {
    setNewRecurringPickups(prev => prev.filter(p => p.id !== tempId));
  };

  const updateSavedCollectionStatusUI = (collectionId, newStatus) => {
    setPickupHistory(prev => prev.map(item => item.id === collectionId ? { ...item, status: newStatus } : item));
    setModifiedSavedCollections(prev => ({ ...prev, [collectionId]: newStatus }));
  };

  const handleDeleteCollection = async (collectionId) => {
    if (!window.confirm("Delete this collection record?")) return;
    try {
      const res = await deleteCollectionRecord(collectionId);
      if (res.success) {
        toast.success("Collection record deleted");
        setPickupHistory(prev => prev.filter(item => item.id !== collectionId));
        const newModified = { ...modifiedSavedCollections };
        delete newModified[collectionId];
        setModifiedSavedCollections(newModified);
      }
    } catch (error) {
      console.error("Error deleting collection:", error);
      toast.error("Failed to delete record");
    }
  };

  const fetchFormOptions = async () => {
    try {
      const [usersRes, wasteRes, slotsRes] = await Promise.all([
        getAllUsers('user'),
        getWasteItems(),
        getTimeSlots()
      ]);
      if (usersRes && usersRes.success) setUsersList(usersRes.users || []);
      if (wasteRes && wasteRes.success) setWasteTypes(wasteRes.items || []);
      if (slotsRes && slotsRes.success) setTimeSlots(slotsRes.slots || []);
    } catch (error) {
      console.error("Error fetching form options:", error);
    }
  };

  const handleMainTabChange = (value) => {
    setMainTab(value);
  };

  const handleOpenScheduleDialog = () => {
    if (usersList.length === 0) {
      fetchFormOptions();
    }
    setIsScheduleDialogOpen(true);
  };

  const handleScheduleSubmit = async () => {
    if (!scheduleForm.userIds || scheduleForm.userIds.length === 0) return toast.error("Please select at least one user");
    if (!scheduleForm.scheduled_date || !scheduleForm.scheduled_time_slot) {
      return toast.error("Date and Time slot are required");
    }
    
    setIsScheduling(true);
    try {
      const res = await createAdminPickup(scheduleForm);
      if (res.success) {
        setScheduleForm({ userIds: [], pickup_type: "normal", waste_type: "", scheduled_date: "", scheduled_time_slot: "", notes: "", amount: "" });
        setUserSearch("");
        setIsScheduleDialogOpen(false);
        
        // Show result popup
        setScheduleResultData(res);
        setIsResultDialogOpen(true);

        const data = await getPickups();
        if (data?.success) setPickups(data.pickups || []);
      } else {
        toast.error(res.message || "Failed to schedule pickup");
      }
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.failures) {
        // Handle 400 response with failure details by showing the popup
        setScheduleResultData(error.response.data);
        setIsResultDialogOpen(true);
      } else {
        toast.error(error.response?.data?.detail || error.response?.data?.message || "Failed to schedule pickup");
      }
    } finally {
      setIsScheduling(false);
    }
  };

  if (loading) return <div className="flex h-screen items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Pickup Tracking</h1>
        <p className="text-muted-foreground mt-1">Real-time tracking and management of waste pickups</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card className={`border-border cursor-pointer hover:ring-2 hover:ring-primary/50 transition-smooth ${activeTab === 'all' ? 'ring-2 ring-primary' : ''}`} onClick={() => setActiveTab("all")}>
          <CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm font-medium text-muted-foreground">Total</p><h3 className="text-2xl font-bold mt-1">{pickups.length}</h3></div><Package className="w-8 h-8 text-primary" /></div></CardContent>
        </Card>
        <Card className={`border-border cursor-pointer hover:ring-2 hover:ring-primary/50 transition-smooth ${activeTab === 'pending' ? 'ring-2 ring-primary' : ''}`} onClick={() => setActiveTab("pending")}>
          <CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm font-medium text-muted-foreground">Pending</p><h3 className="text-2xl font-bold mt-1">{pickups.filter(p => ['requested'].includes(p.status)).length}</h3></div><Clock className="w-8 h-8 text-yellow-500" /></div></CardContent>
        </Card>
        <Card className={`border-border cursor-pointer hover:ring-2 hover:ring-primary/50 transition-smooth ${activeTab === 'in-progress' ? 'ring-2 ring-primary' : ''}`} onClick={() => setActiveTab("in-progress")}>
          <CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm font-medium text-muted-foreground">In Progress</p><h3 className="text-2xl font-bold mt-1">{pickups.filter(p => ['assigned', 'accepted'].includes(p.status)).length}</h3></div><Navigation className="w-8 h-8 text-blue-500" /></div></CardContent>
        </Card>
        <Card className={`border-border cursor-pointer hover:ring-2 hover:ring-primary/50 transition-smooth ${activeTab === 'completed' ? 'ring-2 ring-primary' : ''}`} onClick={() => setActiveTab("completed")}>
          <CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm font-medium text-muted-foreground">Completed</p><h3 className="text-2xl font-bold mt-1">{pickups.filter(p => ['collected', 'completed', 'disposed'].includes(p.status)).length}</h3></div><CheckCircle className="w-8 h-8 text-green-500" /></div></CardContent>
        </Card>
        <Card className={`border-border cursor-pointer hover:ring-2 hover:ring-primary/50 transition-smooth ${activeTab === 'cancelled' ? 'ring-2 ring-primary' : ''}`} onClick={() => setActiveTab("cancelled")}>
          <CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm font-medium text-muted-foreground">Cancelled</p><h3 className="text-2xl font-bold mt-1">{pickups.filter(p => ['cancelled'].includes(p.status)).length}</h3></div><XCircle className="w-8 h-8 text-red-500" /></div></CardContent>
        </Card>
      </div>

      <Card className="border-border">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <CardTitle>Pickup Requests</CardTitle>
              <div className="flex items-center bg-muted rounded-md border border-border overflow-hidden relative">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 rounded-none hover:bg-muted-foreground/10"
                  onClick={() => {
                    const d = new Date(selectedDate);
                    d.setDate(d.getDate() - 1);
                    setSelectedDate(d.toISOString().split('T')[0]);
                  }}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="px-3 py-1 text-sm font-medium border-x border-border/50 min-w-[120px] text-center bg-background relative">
                  {new Date(selectedDate).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                  <Input 
                    type="date"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    value={selectedDate}
                    onChange={(e) => {
                      if (e.target.value) {
                        setSelectedDate(e.target.value);
                      }
                    }}
                  />
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 rounded-none hover:bg-muted-foreground/10"
                  onClick={() => {
                    const d = new Date(selectedDate);
                    d.setDate(d.getDate() + 1);
                    setSelectedDate(d.toISOString().split('T')[0]);
                  }}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={mainTab} onValueChange={handleMainTabChange} className="w-full">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
                <TabsTrigger value="one-time">One-time Pickups</TabsTrigger>
                <TabsTrigger value="recurring">Recurring Pickups</TabsTrigger>
              </TabsList>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="w-full sm:w-[140px]">
                  <Select value={headerSourceFilter} onValueChange={setHeaderSourceFilter}>
                    <SelectTrigger className="h-10 border-border bg-background shadow-sm">
                      <SelectValue placeholder="Source Filter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="admin">Admin Only</SelectItem>
                      <SelectItem value="app">App Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleOpenScheduleDialog} className="bg-primary text-primary-foreground w-full sm:w-auto h-10">
                  <Calendar className="w-4 h-4 mr-2" /> Schedule Pickup
                </Button>
              </div>
            </div>
            
            <div className="border rounded-lg mt-6 overflow-hidden flex flex-col">
              {/* Standalone Global Header with Search */}
              <div className="bg-muted/50 border-b flex items-center px-4 py-3 text-sm font-medium text-muted-foreground">
                <div className="w-[30%] min-w-[200px] pr-4">
                  <div className="relative max-w-md">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input 
                      placeholder="Search user, address..." 
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-8 pl-8 text-xs bg-background border-border/50 focus-visible:ring-1"
                    />
                  </div>
                </div>
                <div className="w-[20%] min-w-[120px] pl-2">Captain</div>
                <div className="w-[15%] min-w-[100px]">Status</div>
                <div className="w-[20%] min-w-[120px]">Source</div>
                <div className="flex-1 text-right min-w-[80px]">Actions</div>
              </div>

              {/* Table Body Area */}
              <div className="w-full">
                <TabsContent value="one-time" className="m-0">
                  {getFilteredPickups("normal").length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">No pickups found.</div>
                  ) : (
                    <Table>
                      <TableBody>
                        {getFilteredPickups("normal").map((pickup) => (
                          <TableRow key={pickup._id || pickup.id} className="hover:bg-muted/50 transition-smooth border-b last:border-0 flex w-full">
                            <TableCell className="w-[30%] min-w-[200px] py-4 align-middle">
                              <div>
                                <p className="font-medium text-foreground">{pickup.user_name || "Unknown User"}</p>
                                <p className="text-xs text-muted-foreground line-clamp-1">{pickup.address || pickup.location?.address || "No Address"}</p>
                              </div>
                            </TableCell>
                            <TableCell className="w-[20%] min-w-[120px] py-4 align-middle">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0">
                                  <User className="w-3 h-3 text-muted-foreground" />
                                </div>
                                <span className="text-sm font-medium truncate">{pickup.worker_name || "Unassigned"}</span>
                              </div>
                            </TableCell>
                            <TableCell className="w-[15%] min-w-[100px] py-4 align-middle">
                              <Badge className={`${getStatusColor(pickup.status)} capitalize`}>{pickup.status}</Badge>
                            </TableCell>
                            <TableCell className="w-[20%] min-w-[120px] py-4 align-middle">
                              <Badge variant="outline" className={`${pickup.createdByAdmin ? 'text-blue-600 border-blue-200 bg-blue-50' : 'text-purple-600 border-purple-200 bg-purple-50'}`}>
                                {pickup.createdByAdmin ? 'Admin' : 'App'}
                              </Badge>
                            </TableCell>
                            <TableCell className="flex-1 py-4 align-middle text-right min-w-[80px]">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openEditDialog(pickup)}><Edit2 className="w-4 h-4 text-muted-foreground hover:text-primary" /></Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDeletePickup(pickup._id || pickup.id)}><Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" /></Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </TabsContent>
                <TabsContent value="recurring" className="m-0">
                  {getFilteredPickups("recurring").length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">No pickups found.</div>
                  ) : (
                    <Table>
                      <TableBody>
                        {getFilteredPickups("recurring").map((pickup) => (
                          <TableRow key={pickup._id || pickup.id} className="hover:bg-muted/50 transition-smooth border-b last:border-0 flex w-full">
                            <TableCell className="w-[30%] min-w-[200px] py-4 align-middle">
                              <div>
                                <p className="font-medium text-foreground">{pickup.user_name || "Unknown User"}</p>
                                <p className="text-xs text-muted-foreground line-clamp-1">{pickup.address || pickup.location?.address || "No Address"}</p>
                              </div>
                            </TableCell>
                            <TableCell className="w-[20%] min-w-[120px] py-4 align-middle">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center shrink-0">
                                  <User className="w-3 h-3 text-muted-foreground" />
                                </div>
                                <span className="text-sm font-medium truncate">{pickup.worker_name || "Unassigned"}</span>
                              </div>
                            </TableCell>
                            <TableCell className="w-[15%] min-w-[100px] py-4 align-middle">
                              <Badge className={`${getStatusColor(pickup.status)} capitalize`}>{pickup.status}</Badge>
                            </TableCell>
                            <TableCell className="w-[20%] min-w-[120px] py-4 align-middle">
                              <Badge variant="outline" className={`${pickup.createdByAdmin ? 'text-blue-600 border-blue-200 bg-blue-50' : 'text-purple-600 border-purple-200 bg-purple-50'}`}>
                                {pickup.createdByAdmin ? 'Admin' : 'App'}
                              </Badge>
                            </TableCell>
                            <TableCell className="flex-1 py-4 align-middle text-right min-w-[80px]">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="icon" onClick={() => openEditDialog(pickup)}><Edit2 className="w-4 h-4 text-muted-foreground hover:text-primary" /></Button>
                                <Button variant="ghost" size="icon" onClick={() => handleDeletePickup(pickup._id || pickup.id)}><Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" /></Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </TabsContent>
              </div>
            </div>
          </Tabs>
        </CardContent>
      </Card>

      {/* Schedule Pickup Dialog */}
      <Dialog open={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schedule New Pickup</DialogTitle>
            <DialogDescription>Schedule a normal pickup on behalf of a user</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="bg-amber-50 text-amber-800 p-4 rounded-md text-sm flex items-start gap-3 border border-amber-200">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-semibold">Important Notes:</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>You must ensure the user's current location is updated before scheduling a pickup for them.</li>
                  <li>You can schedule pickups for a maximum of 10 users at once.</li>
                </ul>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2 relative">
                <label className="text-sm font-medium">Select Users (Max 10)</label>
                <Input 
                  placeholder="Search user by name or phone..." 
                  value={userSearch} 
                  onChange={(e) => { setUserSearch(e.target.value); setShowUserDropdown(true); }}
                  onFocus={() => setShowUserDropdown(true)}
                  onBlur={() => setTimeout(() => setShowUserDropdown(false), 200)}
                />
                {scheduleForm.userIds && scheduleForm.userIds.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {scheduleForm.userIds.map(id => {
                      const u = usersList.find(user => user._id === id);
                      return (
                        <Badge key={id} variant="secondary" className="flex items-center gap-1 py-1">
                          {u ? u.name : id}
                          <button 
                            onClick={() => setScheduleForm({
                              ...scheduleForm,
                              userIds: scheduleForm.userIds.filter(uid => uid !== id)
                            })}
                            className="ml-1 hover:text-destructive"
                          >
                            <XCircle className="w-3 h-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
                {showUserDropdown && (
                  <div className="absolute top-[70px] left-0 w-full bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto z-50">
                    {usersList.filter(u => 
                      (u.name && u.name.toLowerCase().includes(userSearch.toLowerCase())) || 
                      (u.contact && u.contact.includes(userSearch))
                    ).map(u => (
                      <div 
                        key={u._id} 
                        className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() => {
                          if (scheduleForm.userIds && scheduleForm.userIds.includes(u._id)) {
                            toast.error("User already selected");
                          } else if (scheduleForm.userIds && scheduleForm.userIds.length >= 10) {
                            toast.error("Maximum 10 users can be selected");
                          } else {
                            setScheduleForm({
                              ...scheduleForm, 
                              userIds: [...(scheduleForm.userIds || []), u._id]
                            });
                          }
                          setUserSearch("");
                          setShowUserDropdown(false);
                        }}
                      >
                        {u.name} - {u.contact}
                      </div>
                    ))}
                    {usersList.filter(u => 
                      (u.name && u.name.toLowerCase().includes(userSearch.toLowerCase())) || 
                      (u.contact && u.contact.includes(userSearch))
                    ).length === 0 && (
                      <div className="p-2 text-sm text-muted-foreground">No users found</div>
                    )}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Waste Type</label>
                <Select value={scheduleForm.waste_type} onValueChange={(val) => setScheduleForm({...scheduleForm, waste_type: val})}>
                  <SelectTrigger><SelectValue placeholder="Select Waste Type" /></SelectTrigger>
                  <SelectContent>
                    {wasteTypes.map(w => <SelectItem key={w.id} value={w.name}>{w.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Schedule Date</label>
                  <Input type="date" value={scheduleForm.scheduled_date} onChange={(e) => setScheduleForm({...scheduleForm, scheduled_date: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Time Slot</label>
                  <Select value={scheduleForm.scheduled_time_slot} onValueChange={(val) => setScheduleForm({...scheduleForm, scheduled_time_slot: val})}>
                    <SelectTrigger><SelectValue placeholder="Select Time Slot" /></SelectTrigger>
                    <SelectContent>
                      {timeSlots.filter(s => s.isActive !== false).map(s => <SelectItem key={s.id} value={s.value}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Notes</label>
                <Input value={scheduleForm.notes} onChange={(e) => setScheduleForm({...scheduleForm, notes: e.target.value})} placeholder="Any special instructions..." />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Amount (₹) (optional)</label>
                <Input type="number" min="0" step="0.01" value={scheduleForm.amount} onChange={(e) => setScheduleForm({...scheduleForm, amount: e.target.value})} placeholder="Enter amount to charge for this pickup" />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsScheduleDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleScheduleSubmit} disabled={isScheduling}>
              {isScheduling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scheduling...</> : "Schedule Pickup"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5 text-primary" />Pickup Details</DialogTitle>
          </DialogHeader>
          {selectedPickup && (
            <div className="grid gap-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-muted/30 p-3 rounded-lg space-y-3">
                    <div className="flex items-center gap-2"><User className="w-4 h-4 text-primary" /><span className="font-medium">{selectedPickup.user_name || "User"}</span></div>
                    {selectedPickup.user_phone && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                        <span>{selectedPickup.user_phone}</span>
                      </div>
                    )}
                    {selectedPickup.user_email && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><rect width="20" height="16" x="2" y="4" rx="2"></rect><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path></svg>
                        <span>{selectedPickup.user_email}</span>
                      </div>
                    )}
                    {selectedPickup.account_category && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                        <span className="capitalize">{selectedPickup.account_category}</span>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-muted-foreground uppercase">Address</label>
                      <Input value={editableAddress || ""} onChange={(e) => {
                        const nextAddress = e.target.value;
                        setEditableAddress(nextAddress);
                        // Admin-scheduled flow keeps Exact Location in sync with
                        // Address until admin manually overrides Exact Location.
                        // App-scheduled flow keeps the Step-2 location string.
                        if (
                          selectedPickup?.createdByAdmin &&
                          !isLocationManuallyEditedRef.current &&
                          (editableLocationInput || "") === (editableAddress || "")
                        ) {
                          setEditableLocationInput(nextAddress);
                        }
                      }} className="h-9 text-sm" placeholder="Pickup Address" />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold text-muted-foreground uppercase">Exact Location (Link/Coords)</label>
                      <Input value={editableLocationInput || ""} onChange={(e) => {
                        isLocationManuallyEditedRef.current = true;
                        setEditableLocationInput(e.target.value);
                      }} placeholder="Google Maps Link or Coordinates" className="h-9 text-sm" />
                    </div>
                  </div>
                  <div className="bg-muted/30 p-3 rounded-lg space-y-3">
                    <label className="text-[10px] font-bold text-muted-foreground uppercase">Schedule</label>
                    {selectedPickup.pickup_type === 'recurring' ? (
                      <div className="flex items-center gap-2 text-sm"><Repeat className="w-4 h-4 text-primary" /><span>{selectedPickup.frequency}</span></div>
                    ) : (
                      <Input type="date" value={editableDate || ""} onChange={(e) => setEditableDate(e.target.value)} className="h-9 text-sm" />
                    )}
                  </div>
                  <div className="bg-muted/30 p-3 rounded-lg space-y-3">
                    <label className="text-[10px] font-bold text-muted-foreground uppercase">Amount (₹) (optional)</label>
                    <Input 
                      type="number" 
                      min="0" 
                      step="0.01" 
                      value={selectedPickup.amount ?? ""} 
                      onChange={(e) => setSelectedPickup({
                        ...selectedPickup, 
                        original_amount: selectedPickup.original_amount !== undefined ? selectedPickup.original_amount : selectedPickup.amount,
                        amount: e.target.value
                      })} 
                      className="h-9 text-sm" 
                      placeholder="Enter amount"
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="aspect-video relative overflow-hidden bg-muted rounded-lg border">
                    {selectedPickup.photo ? <img src={selectedPickup.photo} alt="Waste" className="object-cover w-full h-full" /> : <div className="flex items-center justify-center w-full h-full text-muted-foreground text-xs">No image</div>}
                  </div>
                  <div className="space-y-3">
                    <div className="space-y-1.5"><label className="text-xs font-medium">Assign Captain</label>
                      <Select value={selectedWorkerId} onValueChange={setSelectedWorkerId}>
                        <SelectTrigger className="w-full h-10 border"><SelectValue placeholder="Select Captain" /></SelectTrigger>
                        <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{workers.map((w) => <SelectItem key={w._id || w.id} value={w._id || w.id}>{w.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5"><label className="text-xs font-medium">Status</label>
                      <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                        <SelectTrigger className={`w-full h-10 ${getStatusColor(selectedStatus)} capitalize border`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="requested">Requested</SelectItem><SelectItem value="assigned">Assigned</SelectItem><SelectItem value="accepted">Accepted</SelectItem><SelectItem value="collected">Collected</SelectItem><SelectItem value="completed">Completed</SelectItem><SelectItem value="disposed">Disposed</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem></SelectContent>
                      </Select>
                    </div>
                    {selectedStatus === 'cancelled' && (
                      <div className="space-y-1.5 animate-in fade-in zoom-in duration-200">
                        <label className="text-xs font-medium text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Cancellation Remark (Required)
                        </label>
                        <Input 
                          value={remark || ""} 
                          onChange={(e) => setRemark(e.target.value)} 
                          placeholder="Reason for cancellation..." 
                          className="h-10 border-red-200 focus-visible:ring-red-500" 
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {selectedPickup.pickup_type === 'recurring' && (
                <div className="border-t pt-4 space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Manage Pickups</h4>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleAddRecurringPickupUI} 
                        className="h-8 gap-1 border-primary/50 text-primary hover:bg-primary/5"
                        disabled={newRecurringPickups.some(p => !['completed', 'disposed'].includes(p.status)) || pickupHistory.some(p => !['completed', 'disposed'].includes(p.status))}
                      >
                        <Plus className="w-3 h-3" /> Add Entry
                      </Button>
                    </div>

                    <div className="space-y-4">
                      {/* Section: Current Pickup (Staged OR Saved In-Progress) */}
                      <h5 className="text-[10px] font-bold text-muted-foreground uppercase">Current Pickup</h5>
                      
                      <div className="space-y-3">
                        {/* Staged entry (Active) */}
                        {newRecurringPickups.filter(p => !['completed', 'disposed'].includes(p.status)).map((item) => (
                          <div key={item.id} className="p-4 bg-yellow-50/50 dark:bg-yellow-900/10 rounded-lg border border-yellow-200/50 space-y-4 animate-in fade-in zoom-in-95 duration-300">
                            <div className="flex items-center justify-between">
                              <Badge className="bg-yellow-500 text-white text-[10px] h-4">New Entry (Staging)</Badge>
                              <button onClick={() => removeNewRecurringPickup(item.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Date</label>
                                <Input 
                                  type="date" 
                                  value={item.date} 
                                  onChange={(e) => updateNewRecurringPickup(item.id, 'date', e.target.value)}
                                  className="h-9 text-sm bg-white"
                                />
                              </div>
                              <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Captain</label>
                                <Select 
                                  value={item.worker_id} 
                                  onValueChange={(val) => updateNewRecurringPickup(item.id, 'worker_id', val)}
                                >
                                  <SelectTrigger className="h-9 text-sm bg-white"><SelectValue placeholder="Captain" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="unassigned">Unassigned</SelectItem>
                                    {workers.map((w) => <SelectItem key={w._id || w.id} value={w._id || w.id}>{w.name}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-muted-foreground uppercase">Status</label>
                                <Select 
                                  value={item.status} 
                                  onValueChange={(val) => updateNewRecurringPickup(item.id, 'status', val)}
                                >
                                  <SelectTrigger className="h-9 text-sm capitalize bg-white"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="requested">Requested</SelectItem>
                                    <SelectItem value="assigned">Assigned</SelectItem>
                                    <SelectItem value="accepted">Accepted</SelectItem>
                                    <SelectItem value="collected">Collected</SelectItem>
                                    <SelectItem value="disposed">Disposed</SelectItem>
                                    <SelectItem value="completed">Completed</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                        ))}

                        {/* Saved In-Progress entry from backend */}
                        {pickupHistory.filter(p => !['completed', 'disposed'].includes(p.status)).map((item) => (
                          <div key={item.id} className="p-4 bg-muted/30 rounded-lg border border-border space-y-4">
                            <div className="flex items-center justify-between">
                              <Badge variant="outline" className="text-[10px] h-4">Saved Entry (In-Progress)</Badge>
                              <button onClick={() => handleDeleteCollection(item.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="flex items-center gap-3">
                                <Calendar className="w-4 h-4 text-primary" />
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium">{new Date(item.collected_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}</span>
                                  <div className="space-y-1">
                                    <label className="text-[8px] font-bold text-muted-foreground uppercase">Update Status</label>
                                    <Select 
                                      value={item.status} 
                                      onValueChange={(val) => updateSavedCollectionStatusUI(item.id, val)}
                                    >
                                      <SelectTrigger className={`h-8 text-[10px] capitalize ${getStatusColor(item.status)}`}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="requested">Requested</SelectItem>
                                        <SelectItem value="assigned">Assigned</SelectItem>
                                        <SelectItem value="accepted">Accepted</SelectItem>
                                        <SelectItem value="collected">Collected</SelectItem>
                                        <SelectItem value="disposed">Disposed</SelectItem>
                                        <SelectItem value="completed">Completed</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-xs font-bold text-foreground truncate max-w-[150px]">{item.worker_name || "Unassigned"}</div>
                                <div className="text-[10px] text-muted-foreground">Assigned Captain</div>
                              </div>
                            </div>
                          </div>
                        ))}

                        {newRecurringPickups.filter(p => !['completed', 'disposed'].includes(p.status)).length === 0 && 
                         pickupHistory.filter(p => !['completed', 'disposed'].includes(p.status)).length === 0 && (
                          <div className="text-center py-6 bg-muted/10 rounded-lg border border-dashed flex flex-col items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-muted-foreground/50" />
                            <span className="text-xs text-muted-foreground">No active pickup entry. Click "Add Entry" to start.</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-4 pt-2">
                      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Collection History</h4>
                      
                      <div className="space-y-3">
                        {/* Section: History (Saved + Staged Completed/Disposed) */}
                        
                        {/* Staged entries that are ready to be history */}
                        {newRecurringPickups.filter(item => ['completed', 'disposed'].includes(item.status)).map((item) => (
                          <div key={item.id} className="p-3 bg-primary/5 rounded-lg border border-primary/20 flex items-center justify-between animate-in fade-in slide-in-from-top-1 duration-300">
                            <div className="flex items-center gap-3">
                              <Calendar className="w-4 h-4 text-primary" />
                              <div className="flex flex-col">
                                <span className="text-sm font-medium">{new Date(item.date).toLocaleDateString(undefined, { dateStyle: 'medium' })}</span>
                                <div className="flex items-center gap-2">
                                  <Badge className={`${getStatusColor(item.status)} text-[8px] h-4 px-1 capitalize`}>{item.status}</Badge>
                                  <Badge className="bg-primary text-[8px] h-4 px-1 font-bold uppercase tracking-tighter text-white">Pending Save</Badge>
                                </div>
                              </div>
                            </div>
                              <div className="text-right flex items-center gap-4">
                                <div className="text-right">
                                  <div className="text-xs font-bold text-foreground truncate max-w-[100px]">
                                    {item.worker_id === "unassigned" ? "Unassigned" : workers.find(w => w._id === item.worker_id || w.id === item.worker_id)?.name || "Captain"}
                                  </div>
                                  <div className="text-[10px] text-muted-foreground">Assigned Captain</div>
                                </div>
                              <button 
                                onClick={() => removeNewRecurringPickup(item.id)}
                                className="text-muted-foreground hover:text-destructive transition-colors p-1"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ))}

                        {/* Existing history records from backend - ONLY Completed/Disposed */}
                        {loadingHistory ? (
                          <div className="text-center py-4"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                        ) : (pickupHistory.filter(p => ['completed', 'disposed'].includes(p.status)).length > 0 || newRecurringPickups.some(item => ['completed', 'disposed'].includes(item.status))) ? (
                          pickupHistory.filter(p => ['completed', 'disposed'].includes(p.status)).map((item) => (
                            <div key={item.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border border-border/50">
                              <div className="flex items-center gap-3">
                                <Calendar className="w-4 h-4 text-primary" />
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium">{new Date(item.collected_at).toLocaleDateString(undefined, { dateStyle: 'medium' })}</span>
                                  <div className="flex items-center gap-2">
                                    <Badge className={`${getStatusColor(item.status)} text-[8px] h-4 px-1 capitalize`}>{item.status}</Badge>
                                    {['completed', 'disposed'].includes(item.status) && (
                                      <Badge variant="outline" className="text-[8px] h-4 px-1 bg-green-50 text-green-700 border-green-200 font-bold uppercase tracking-tighter">Done</Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right flex items-center gap-4">
                                <div className="text-right">
                                  <div className="text-xs font-bold text-foreground truncate max-w-[100px]">{item.worker_name || "Unassigned"}</div>
                                  <div className="text-[10px] text-muted-foreground">Assigned Captain</div>
                                </div>
                                <button 
                                  onClick={() => handleDeleteCollection(item.id)}
                                  className="text-muted-foreground hover:text-destructive transition-colors p-1"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8 text-xs text-muted-foreground bg-muted/10 rounded-lg border border-dashed flex flex-col items-center gap-2">
                            <Package className="w-8 h-8 opacity-20" />
                            <span>No collection records found.</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {selectedPickup.description && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Notes/Description</h4>
                  <div className="bg-muted/30 p-3 rounded-lg text-sm italic text-muted-foreground">
                    "{selectedPickup.description}"
                  </div>
                </div>
              )}

              {selectedPickup.remark && selectedStatus !== 'cancelled' && (
                <div className="border-t pt-4 animate-in fade-in duration-200">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">Previous Remark</h4>
                  <div className="bg-muted/30 p-3 rounded-lg text-sm italic text-muted-foreground border border-border/50">
                    "{selectedPickup.remark}"
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2 border-t pt-4 mt-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSaving}>Cancel</Button>
            <Button onClick={handleApplyChanges} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Applying...
                </>
              ) : "Apply Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Scheduling Results Dialog */}
      <Dialog open={isResultDialogOpen} onOpenChange={setIsResultDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Scheduling Results</DialogTitle>
            <DialogDescription>Overview of the requested pickups</DialogDescription>
          </DialogHeader>
          
          {scheduleResultData && (
            <div className="space-y-6 py-4">
              <div className="flex items-center gap-4">
                <div className="flex-1 bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-green-700">{scheduleResultData.scheduledCount || 0}</div>
                  <div className="text-sm font-medium text-green-800">Successfully Scheduled</div>
                </div>
                
                <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-red-700">{scheduleResultData.failedCount || 0}</div>
                  <div className="text-sm font-medium text-red-800">Failed</div>
                </div>
              </div>

              {scheduleResultData.failures && scheduleResultData.failures.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Failure Details</h4>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableBody>
                        {scheduleResultData.failures.map((failure, idx) => (
                          <TableRow key={idx} className="bg-red-50/30">
                            <TableCell className="py-3 font-medium text-sm">
                              {failure.name || failure.userId}
                            </TableCell>
                            <TableCell className="py-3 text-sm text-red-600">
                              {failure.reason}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setIsResultDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Pickups;
